import React from 'react';

// Components
import KpiCardsSection from '../../components/kpicardssection';
import KpiDrawer from '../../components/kpidrawer/KpiDrawer';
import SkeletonCards from '../../components/skeletons/operations/SkeletonCards';
import SkeletonDataGrid from '../../components/skeletons/common/SkeletonDataGrid';

// Tab Components
import Inventory from './tabs/inventory/Inventory';
import Marketing from './tabs/marketing/Marketing';
import People from './tabs/people/People';
import B2B from './tabs/b2b/B2B';

// Redux
import {
  selectAllKpiCards,
  selectCardLoading,
  selectKpiDashboardIsNull,
} from '../../redux/OperationsReducer/operationsSlice';
import { useAppSelector } from '../../redux';

// Models
import { Card, TabData } from '../../models/operationsmodels/KpiModels';

// Languages
import lang from '../../languages/en.json';

// Utils
import { getGaugeColor } from '../../utils/gaugeUtils';
import { useClickHandler } from '../../utils/usageMetricsLogger';

// Styles
import styles from './Operations.module.css';
import pagesStandardStyles from '../Pages.module.css';

const PAGE_NM = 'Operations';

/**
 * Maps KPI card labels to their corresponding React components
 * @param label - The card title/label from KPI data
 * @returns Corresponding React component or fallback
 */
const getComponentForLabel = (label: string): React.ReactNode => {
  const normalizedLabel = label.toUpperCase().trim();

  switch (normalizedLabel) {
    case lang.operations.labels.inventory:
      return <Inventory />;
    case lang.operations.labels.marketing:
      return <Marketing />;
    case lang.operations.labels.people:
      return <People />;
    case lang.operations.labels.b2b:
      return <B2B />;
    default:
      return (
        <div className={styles.notFound}>
          <p>{lang.operations.messages.componentNotImplemented.replace('{label}', label)}</p>
        </div>
      );
  }
};

/**
 * Operations Dashboard Component
 *
 * Main operations dashboard that displays KPI cards with gauge visualizations
 * for different operational areas (Inventory, Marketing, People, B2B).
 *
 * Features:
 * - Dynamic KPI card generation from Redux store
 * - Interactive gauge visualizations with color-coded performance
 * - Tab-based navigation between operational areas
 * - Drawer component for detailed KPI analysis
 * - Skeleton loading states
 *
 * @returns JSX element representing the operations dashboard
 */
const Operations: React.FC = () => {
  // State management
  const [selectedTab, setSelectedTab] = React.useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(false);

  // Select KPI cards data and loading state from Redux store
  const kpiCards = useAppSelector(selectAllKpiCards);
  const isLoading = useAppSelector(selectCardLoading);
  const hasError = useAppSelector(selectKpiDashboardIsNull);

  // Usage metrics click handler for Operations page
  const handleAndLogClick = useClickHandler(PAGE_NM);

  // Generate tabs from KPI data (memoized for performance)
  const tabs: TabData[] = React.useMemo(() => {
    return kpiCards.map((kpiCard: Card) => {
      const label = kpiCard.cardTitle || lang.operations.labels.unknown;
      const hasData = kpiCard.targetKpi > 0;
      const currentKpi = kpiCard.currentKpi || 0;
      const targetKpi = kpiCard.targetKpi || 1;

      return {
        label,
        content: getComponentForLabel(label),
        current: currentKpi,
        target: targetKpi,
        kpiColor: getGaugeColor(currentKpi, targetKpi),
        hasData,
      };
    });
  }, [kpiCards]);

  // Auto-select first tab when data loads
  React.useEffect(() => {
    if (tabs.length > 0 && selectedTab === null) {
      setSelectedTab(0);
    }
  }, [tabs.length, selectedTab]);

  /**
   * Handles tab selection
   * @param index - Index of the selected tab
   */
  const handleTabSelection = React.useCallback((index: number): void => {
    setSelectedTab(index);
  }, []);

  /**
   * Handles drawer toggle
   * React.useCallback memoizes this function to prevent unnecessary re-renders
   */
  const handleDrawerToggle = React.useCallback((): void => {
    setIsDrawerOpen(true);
  }, []);

  /**
   * Handles drawer close
   * React.useCallback memoizes this function to prevent unnecessary re-renders
   */
  const handleDrawerClose = React.useCallback((): void => {
    setIsDrawerOpen(false);
  }, []);

  // Priority order: Loading -> Error -> No Data -> Show Data

  // 1. Show skeleton while loading
  if (isLoading) {
    return (
      <div className={styles.container}>
        <SkeletonCards count={4} />
        <div className={styles.skeletonGrid}>
          <SkeletonDataGrid />
        </div>
      </div>
    );
  }

  // 2. Show error message if API call failed (kpiDashboard is null)
  if (hasError) {
    const errorMessage = lang.pageContent.dataUnavailableError.replace(
      '{section}',
      lang.header.operationsLabel,
    );
    return (
      <div className={pagesStandardStyles.errorContainer}>
        <p>{errorMessage}</p>
      </div>
    );
  }

  // 3. Show "no data" message if API succeeded but returned empty data
  if (kpiCards.length === 0) {
    return (
      <div className={pagesStandardStyles.errorContainer}>
        <p>{lang.pageContent.noData}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* KPI Cards Section */}
      <KpiCardsSection
        tabs={tabs}
        selectedTabIndex={selectedTab}
        onCardClick={index =>
          handleAndLogClick(`KPI Card - ${kpiCards[index]?.cardTitle ?? ''}`, {
            callback: () => handleTabSelection(index),
          })
        }
      />

      {/* View Details Link */}
      <div
        className={styles.viewDetails}
        onClick={() => handleAndLogClick('View Details', { callback: handleDrawerToggle })}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.repeat) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAndLogClick('View Details', { callback: handleDrawerToggle });
          }
        }}>
        {lang.operations.actions.viewDetails}
      </div>

      {/* Selected Tab Content */}
      {selectedTab !== null && tabs[selectedTab] && (
        <div className={styles.contentSection}>{tabs[selectedTab].content}</div>
      )}

      {/* KPI Details Drawer */}
      <KpiDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        tabs={tabs}
      />
    </div>
  );
};

export default Operations;
